/**
 * RTLProvider Component
 *
 * Wraps the application to provide automatic RTL/LTR layout switching based on
 * the active i18next language.
 *
 * What it does:
 *  1. Listens for language changes via react-i18next.
 *  2. Sets the `dir` and `lang` attributes on <html> so native browser features
 *     (scrollbar placement, text selection, etc.) respect the direction.
 *  3. Creates an MUI theme with the correct `direction` value so MUI components
 *     mirror paddings, margins, and flex order automatically.
 *  4. Provides a prefixed Emotion cache using `stylis-plugin-rtl` when in RTL
 *     mode. This plugin automatically flips CSS properties (margin-left
 *     becomes margin-right, etc.) for all Emotion-generated styles.
 *
 * NOTE: `stylis-plugin-rtl` and `@emotion/cache` must be installed as
 * dependencies for the RTL CSS transformation to work.
 *
 * If `stylis-plugin-rtl` is not installed the provider will still set the
 * correct `dir` attribute and MUI theme direction, but Emotion-generated styles
 * will not be auto-flipped.  Install with:
 *   npm install stylis-plugin-rtl @emotion/cache
 */
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, createTheme, Theme } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache, { EmotionCache } from '@emotion/cache';
import { isRTL } from './index';

// ---------------------------------------------------------------------------
// Try to import the RTL stylis plugin.  If it is not installed we fall back
// to a no-op so the application still works (just without automatic CSS
// flipping).
// ---------------------------------------------------------------------------
let stylisRTLPlugin: any;
try {
  // stylis-plugin-rtl ships a default export
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  stylisRTLPlugin = require('stylis-plugin-rtl').default ?? require('stylis-plugin-rtl');
} catch {
  // Package not installed -- RTL CSS flipping will be unavailable.
  stylisRTLPlugin = null;
}

// ---------------------------------------------------------------------------
// Emotion caches (created once and reused)
// ---------------------------------------------------------------------------
const makeLTRCache = (): EmotionCache =>
  createCache({ key: 'mui-ltr' });

const makeRTLCache = (): EmotionCache => {
  if (stylisRTLPlugin) {
    return createCache({
      key: 'mui-rtl',
      stylisPlugins: [stylisRTLPlugin],
    });
  }
  // Fallback -- no CSS flipping but direction still works via MUI theme
  return createCache({ key: 'mui-rtl-fallback' });
};

// ---------------------------------------------------------------------------
// Base MUI theme — editorial "ink on warm paper" palette with a burgundy
// precision accent, mirroring the procurement app's design system.
// The direction property is set dynamically below.
// ---------------------------------------------------------------------------
const baseThemeOptions = {
  palette: {
    mode: 'light' as const,
    // Primary is the graphite ink used for default fills and body text —
    // burgundy is reserved as the precision accent (secondary), mirroring
    // procurement's editorial "ink on paper, accent used sparingly" system.
    primary: {
      main: '#15192A',
      light: '#3C4150',
      dark: '#15192A',
    },
    secondary: {
      main: '#8B2438',
      light: '#FAEEF0',
      dark: '#6E1B2C',
    },
    background: {
      default: '#F7F5EF',
      paper: '#FFFFFF',
    },
    success: {
      main: '#16A34A',
      light: '#F0FDF4',
    },
    warning: {
      main: '#CA8A04',
      light: '#FEFCE8',
    },
    error: {
      main: '#DC2626',
      light: '#FEF2F2',
    },
    info: {
      main: '#2563EB',
      light: '#EFF6FF',
    },
    text: {
      primary: '#15192A',
      secondary: '#3C4150',
      disabled: '#A9AEBB',
    },
    divider: '#E4E0D4',
  },
  typography: {
    fontFamily: '"Geist", "Segoe UI", system-ui, sans-serif',
    fontSize: 13,
    h1: { fontWeight: 700, fontSize: '2rem', letterSpacing: '-0.01em', lineHeight: 1.25 },
    h2: { fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.01em', lineHeight: 1.25 },
    h3: { fontWeight: 600, fontSize: '1.25rem', letterSpacing: '-0.01em', lineHeight: 1.25 },
    h4: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.25 },
    h5: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.25 },
    h6: { fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.25 },
    subtitle1: { fontSize: '0.8125rem', fontWeight: 500, color: '#3C4150' },
    subtitle2: { fontSize: '0.75rem', fontWeight: 500, color: '#6B7080' },
    body1: { fontSize: '0.8125rem', lineHeight: 1.5, color: '#3C4150' },
    body2: { fontSize: '0.75rem', lineHeight: 1.5, color: '#3C4150' },
    caption: { fontSize: '0.6875rem', lineHeight: 1.5, color: '#6B7080', letterSpacing: '0.025em' },
    overline: { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const, color: '#6B7080' },
    button: { fontWeight: 600, fontSize: '0.8125rem', textTransform: 'none' as const },
  },
  shape: {
    borderRadius: 6,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(0,0,0,0.04)',
    '0 2px 8px rgba(0,0,0,0.06)',
    '0 4px 16px rgba(0,0,0,0.08)',
    '0 8px 32px rgba(0,0,0,0.10)',
    ...Array(20).fill('0 8px 32px rgba(0,0,0,0.10)'),
  ] as any,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none' as const,
          fontWeight: 500,
          fontSize: '0.8125rem',
          letterSpacing: '-0.01em',
          height: 36,
          padding: '0 16px',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          transition: 'all 120ms ease-out',
        },
        containedSecondary: {
          backgroundColor: '#8B2438',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#6E1B2C',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: '"Geist", "Segoe UI", system-ui, sans-serif',
        },
      },
    },
    // Global scrolling behavior for EVERY table in the app.
    //
    //  - overflowX: 'auto'   → horizontal scrollbar when columns don't fit
    //  - overflowY: 'auto'   → vertical scroll happens INSIDE the container
    //                          (not the page) which is the prerequisite for
    //                          sticky table headers to actually stick.
    //  - maxHeight           → caps the container height relative to the
    //                          viewport so the horizontal scrollbar at the
    //                          bottom of the container is always on screen
    //                          without having to scroll to the end of the
    //                          table. 280px accounts for the app header,
    //                          page title, filters, and some padding.
    //
    // The themed scrollbars (burgundy/paper) make it discoverable that the
    // table can be scrolled both directions.
    MuiTableContainer: {
      styleOverrides: {
        root: {
          width: '100%',
          maxWidth: '100%',
          maxHeight: 'calc(100vh - 280px)',
          overflowX: 'auto' as const,
          overflowY: 'auto' as const,
          '&::-webkit-scrollbar': { height: 12, width: 12 },
          '&::-webkit-scrollbar-track': { background: '#E9E5D9' },
          '&::-webkit-scrollbar-thumb': {
            background: '#8B2438',
            borderRadius: 6,
            border: '2px solid #E9E5D9',
          },
          '&::-webkit-scrollbar-thumb:hover': { background: '#6E1B2C' },
          scrollbarColor: '#8B2438 #E9E5D9',
          scrollbarWidth: 'thin' as const,
        },
      },
    },
    // Partner rule for the override above. Without a min-width the default
    // MUI <Table> shrinks its columns to fit the TableContainer, so the
    // container never overflows and the horizontal scrollbar never appears.
    // Setting min-width to `max-content` makes the table take its natural
    // column width, overflowing the container (and triggering the scrollbar)
    // whenever the viewport is narrower than the sum of the columns.
    // When the container has room to spare, the table's default
    // `width: 100%` still wins, so layouts with few columns are unaffected.
    MuiTable: {
      styleOverrides: {
        root: {
          minWidth: 'max-content',
        },
      },
    },
    // Freeze the header row on every table. MUI's built-in `stickyHeader`
    // prop does the same thing but only when opted in per-<Table>; doing it
    // in the theme means we don't have to touch ~90 component files.
    //
    // position: sticky must live on the <th>/<td> elements themselves (not
    // on <thead>) because CSS table layout rules don't let thead be a
    // positioning context. The sticky works because the scroll parent is
    // the TableContainer (overflow: auto + maxHeight above), not the page.
    //
    // A solid backgroundColor is required so that rows scrolling underneath
    // the sticky header don't show through. We use the paper background
    // from the theme; tables that want a colored header can override via sx.
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            position: 'sticky' as const,
            top: 0,
            zIndex: 2,
            backgroundColor: '#FFFFFF',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontSize: '0.875rem',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E4E0D4',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#8B2438',
            borderWidth: '1.5px',
          },
        },
        notchedOutline: {
          borderColor: '#E4E0D4',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          fontWeight: 500,
          color: '#3C4150',
          '&.Mui-focused': {
            color: '#8B2438',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 500,
          fontSize: '0.8125rem',
          minHeight: 44,
          color: '#3C4150',
          '&.Mui-selected': {
            color: '#8B2438',
            fontWeight: 600,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#8B2438',
          height: 2,
        },
      },
    },
  },
};

// ---------------------------------------------------------------------------
// Provider component
// ---------------------------------------------------------------------------
interface RTLProviderProps {
  children: React.ReactNode;
}

const RTLProvider: React.FC<RTLProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();

  const currentLanguage = i18n.language || 'en';
  const rtl = isRTL(currentLanguage);
  const direction: 'rtl' | 'ltr' = rtl ? 'rtl' : 'ltr';

  // ---- Side-effects on the document element ----
  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = currentLanguage;

    // When switching to Arabic set a common Arabic web-safe font stack
    if (rtl) {
      document.body.style.fontFamily =
        '"Noto Sans Arabic", "Segoe UI", "Tahoma", "Arial", sans-serif';
    } else {
      document.body.style.fontFamily = '';
    }
  }, [direction, currentLanguage, rtl]);

  // ---- Memoised MUI theme with correct direction ----
  const theme: Theme = useMemo(
    () =>
      createTheme({
        ...baseThemeOptions,
        direction,
        typography: {
          ...baseThemeOptions.typography,
          // Extend font family for Arabic when in RTL mode
          fontFamily: rtl
            ? '"Noto Sans Arabic", "Geist", "Segoe UI", system-ui, sans-serif'
            : '"Geist", "Segoe UI", system-ui, sans-serif',
        },
      }),
    [direction, rtl],
  );

  // ---- Memoised Emotion cache ----
  const cache: EmotionCache = useMemo(
    () => (rtl ? makeRTLCache() : makeLTRCache()),
    [rtl],
  );

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </CacheProvider>
  );
};

export default RTLProvider;
